import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useModalA11y } from "../../hooks/useModalA11y";

const ConfirmContext = createContext({
  confirm: async () => false
});

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);
  const modalRef = useRef(null);
  const confirmButtonRef = useRef(null);

  useModalA11y({
    isOpen: Boolean(dialog),
    onClose: () => {
      if (!dialog) return;
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
      setDialog(null);
    },
    containerRef: modalRef,
    initialFocusRef: confirmButtonRef
  });

  const closeWith = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
    setDialog(null);
  }, []);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: options?.title || "Please confirm",
        message: options?.message || "Are you sure?",
        confirmLabel: options?.confirmLabel || "Confirm",
        cancelLabel: options?.cancelLabel || "Cancel",
        danger: options?.danger === true
      });
    });
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {dialog && (
        <div className="modal-overlay" onClick={() => closeWith(false)}>
          <div
            className="glass modal-content confirm-modal"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-message"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="header-flex">
              <h2 id="confirm-title" style={{ margin: 0 }}>{dialog.title}</h2>
            </div>
            <p id="confirm-message" style={{ opacity: 0.82 }}>{dialog.message}</p>
            <div className="confirm-actions">
              <button className="btn-nav" type="button" onClick={() => closeWith(false)}>
                {dialog.cancelLabel}
              </button>
              <button
                className={`btn-nav ${dialog.danger ? "danger-btn" : "success-btn"}`}
                type="button"
                ref={confirmButtonRef}
                onClick={() => closeWith(true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
